import type {
  ChatMessagePart,
  ChatMessagePartData,
} from '../_types/messageParts';

// GraphQL returns a tagged union with all fields nullable
// This type represents what we receive from GraphQL
export type GraphQLMessagePart = {
  id: string;
  type: string;
  sequence: number;
  // Text & Reasoning
  text?: string | null;
  state?: string | null;
  // Tool calls
  toolCallId?: string | null;
  toolName?: string | null;
  toolCallState?: string | null;
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
  errorText?: string | null;
  providerExecuted?: boolean | null;
  // Sources
  sourceId?: string | null;
  url?: string | null;
  title?: string | null;
  // Files
  mediaType?: string | null;
  filename?: string | null;
  // Data
  dataType?: string | null;
  data?: Record<string, unknown> | null;
  // Metadata
  providerMetadata?: Record<string, unknown> | null;
  // Optional typename (DeepOmit may remove this)
  __typename?: 'AssistantChatMessagePart';
};

/**
 * Converts a GraphQL tagged union part to a TypeScript discriminated union
 */
export function convertGraphQLPartToTypescript(
  gqlPart: GraphQLMessagePart,
): ChatMessagePart {
  const base = {
    id: gqlPart.id,
    sequence: gqlPart.sequence,
  };

  let data: ChatMessagePartData;

  switch (gqlPart.type) {
    case 'Text':
      data = {
        type: 'Text',
        text: gqlPart.text ?? '',
        state: (gqlPart.state as 'Streaming' | 'Done') ?? undefined,
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'Reasoning':
      data = {
        type: 'Reasoning',
        text: gqlPart.text ?? '',
        state: (gqlPart.state as 'Streaming' | 'Done') ?? undefined,
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'ToolCall':
      data = {
        type: 'ToolCall',
        toolCallId: gqlPart.toolCallId ?? '',
        toolName: gqlPart.toolName ?? '',
        toolCallState:
          (gqlPart.toolCallState as
            | 'InputStreaming'
            | 'InputAvailable'
            | 'OutputAvailable'
            | 'OutputError') ?? 'InputAvailable',
        input: gqlPart.input ?? undefined,
        output: gqlPart.output ?? undefined,
        errorText: gqlPart.errorText ?? undefined,
        providerExecuted: gqlPart.providerExecuted ?? undefined,
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'DynamicTool':
      data = {
        type: 'DynamicTool',
        toolCallId: gqlPart.toolCallId ?? '',
        toolName: gqlPart.toolName ?? '',
        toolCallState:
          (gqlPart.toolCallState as
            | 'InputStreaming'
            | 'InputAvailable'
            | 'OutputAvailable'
            | 'OutputError') ?? 'InputAvailable',
        input: gqlPart.input ?? undefined,
        output: gqlPart.output ?? undefined,
        errorText: gqlPart.errorText ?? undefined,
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'SourceUrl':
      data = {
        type: 'SourceUrl',
        sourceId: gqlPart.sourceId ?? '',
        url: gqlPart.url ?? '',
        title: gqlPart.title ?? undefined,
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'SourceDocument':
      data = {
        type: 'SourceDocument',
        sourceId: gqlPart.sourceId ?? '',
        mediaType: gqlPart.mediaType ?? '',
        title: gqlPart.title ?? '',
        filename: gqlPart.filename ?? undefined,
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'File':
      data = {
        type: 'File',
        mediaType: gqlPart.mediaType ?? '',
        filename: gqlPart.filename ?? '',
        url: gqlPart.url ?? '',
        providerMetadata: gqlPart.providerMetadata ?? undefined,
      };
      break;

    case 'Data':
      data = {
        type: 'Data',
        dataType: gqlPart.dataType ?? '',
        data: gqlPart.data ?? {},
      };
      break;

    case 'StepStart':
      data = {
        type: 'StepStart',
      };
      break;

    default:
      // Fallback: treat unknown types as StepStart
      console.warn(`Unknown part type: ${gqlPart.type}`);
      data = {
        type: 'StepStart',
      };
  }

  return {
    ...base,
    data,
  };
}

/**
 * Converts an array of GraphQL parts to TypeScript parts
 */
export function convertGraphQLPartsToTypescript(
  gqlParts: GraphQLMessagePart[],
): ChatMessagePart[] {
  return gqlParts.map(convertGraphQLPartToTypescript);
}
